import { BRAND } from "constants/brand";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Form } from "react-bootstrap";
import { Ring } from "@uiball/loaders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLock,
    faCircleInfo,
    faTriangleExclamation,
    faRotateRight,
} from "@fortawesome/free-solid-svg-icons";

import { formatMoney } from "utils/money";
import "./walletPanel.scss";
import { formatDateTime } from "utils/dateFormat";

/**
 * THE WALLET, drawn once for drivers and restaurants (api#147, admin#41).
 *
 * The driver wallet and the restaurant wallet had two copies of this screen,
 * and the copies drifted: one column said «المشرف» and the other «بواسطة», an
 * adjustment read «+0» in one, and the driver history overflowed its modal.
 * One component means the two cannot drift again. The callers pass only their
 * data and their words.
 *
 * NOTHING HERE DECIDES MONEY. The balance, the notices and the history are the
 * server's, read again after every action. The "balance after" line is a
 * preview for the operator; the server writes the real figure.
 *
 * @typedef {object} WalletNotice
 * @property {"danger"|"warning"|"info"} tone
 * @property {string} [title]
 * @property {string} text
 * @property {string} [hint]
 *
 * @param {object} props
 * @param {number} props.balance Signed, in ILS.
 * @param {WalletNotice[]} [props.notices] Shown above the balance, in order.
 * @param {object[]} props.transactions Ledger rows: type, amount, balance_after,
 *   orderId, rate, note, createdAt, and `actor` or `admin` ({id, name}).
 * @param {Record<string, string>} props.typeLabels Arabic name of each type.
 * @param {number} props.page
 * @param {number} props.pages
 * @param {Function} props.onPageChange (page) => void
 * @param {boolean} props.loading
 * @param {boolean} [props.isFetching]
 * @param {string|null} [props.error]
 * @param {Function} [props.onRetry]
 * @param {boolean} [props.canTopUp] Shows the شحن / خصم form. Admin only.
 * @param {Function} [props.onTopUp] ({amount, note}) => Promise<boolean>. True clears the form.
 * @param {Function} [props.onDeduct] ({amount, note}) => Promise<boolean>. True clears the form.
 * @param {boolean} [props.topUpPending] True while either action is in flight.
 * @param {string} [props.zeroWarning] Shown before a deduction that takes the
 *   balance to ₪0 or below: what that stops, in this account's words.
 * @param {Function} [props.showMessage] For the input check only.
 * @param {boolean} [props.linkOrders] Order ids link to the order page (admin).
 */
const WalletPanel = ({
    balance,
    notices = [],
    transactions,
    typeLabels,
    page,
    pages,
    onPageChange,
    loading,
    isFetching = false,
    error = null,
    onRetry,
    canTopUp = false,
    onTopUp,
    onDeduct,
    topUpPending = false,
    zeroWarning = "",
    showMessage,
    linkOrders = false,
}) => {
    const [mode, setMode] = useState("topup");
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");

    const isDeduction = mode === "deduct";
    const entered = Number(amount);
    const hasAmount = amount !== "" && Number.isFinite(entered) && entered > 0;
    const current = Number(balance ?? 0);
    const after = hasAmount ? current + (isDeduction ? -entered : entered) : null;

    const submit = async (event) => {
        event.preventDefault();

        // Checked here so the operator hears about it before a round trip. The
        // server is still the gate: it refuses the same things.
        if (!hasAmount) {
            showMessage?.("error", "هناك خطأ", "أدخل مبلغاً أكبر من صفر");
            return;
        }
        if (isDeduction && !note.trim()) {
            showMessage?.("error", "هناك خطأ", "اكتب سبب الخصم، فهو يظهر في سجل الحركات");
            return;
        }

        const action = isDeduction ? onDeduct : onTopUp;
        if (await action({ amount: entered, note: note.trim() })) {
            setAmount("");
            setNote("");
        }
    };

    if (loading) {
        return (
            <div className="loading-spinner center">
                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
            </div>
        );
    }

    if (error && (!transactions || transactions.length === 0) && balance === null) {
        return (
            <div className="wallet-panel__error surface-card">
                <p>{error}</p>
                {onRetry ? (
                    <button type="button" className="cancel" onClick={() => onRetry()}>
                        <FontAwesomeIcon icon={faRotateRight} /> إعادة المحاولة
                    </button>
                ) : null}
            </div>
        );
    }

    const noticeIcon = { danger: faLock, warning: faTriangleExclamation, info: faCircleInfo };

    let submitLabel = isDeduction ? "خصم من الرصيد" : "شحن رصيد";
    if (topUpPending) submitLabel = isDeduction ? "جاري الخصم..." : "جاري الشحن...";

    return (
        <div className="wallet-panel">
            {notices.map((notice) => (
                <div
                    key={`${notice.tone}-${notice.title || notice.text}`}
                    className={`wallet-panel__notice wallet-panel__notice--${notice.tone}`}
                    role={notice.tone === "info" ? undefined : "alert"}
                >
                    <FontAwesomeIcon icon={noticeIcon[notice.tone]} />
                    <div>
                        {notice.title ? <strong>{notice.title}</strong> : null}
                        <p>{notice.text}</p>
                        {notice.hint ? (
                            <p className="wallet-panel__notice-hint">{notice.hint}</p>
                        ) : null}
                    </div>
                </div>
            ))}

            <div className="wallet-panel__balance surface-card">
                <span className="wallet-panel__balance-label">
                    الرصيد الحالي{isFetching ? " (جاري التحديث...)" : ""}
                </span>
                {/* Zero and a debt are the two balances that matter most: at
                    either one the account takes no new work. */}
                <span
                    className={`wallet-panel__balance-value${
                        current < 0
                            ? " wallet-panel__debit"
                            : current === 0
                              ? " wallet-panel__zero"
                              : ""
                    }`}
                >
                    {formatMoney(current)}
                </span>
            </div>

            {canTopUp ? (
                <Form className="form wallet-panel__form" onSubmit={submit}>
                    {onDeduct ? (
                        <div className="wallet-panel__modes" role="group" aria-label="نوع العملية">
                            <button
                                type="button"
                                className={isDeduction ? "cancel" : "button"}
                                aria-pressed={!isDeduction}
                                onClick={() => setMode("topup")}
                            >
                                شحن
                            </button>
                            <button
                                type="button"
                                className={isDeduction ? "button" : "cancel"}
                                aria-pressed={isDeduction}
                                onClick={() => setMode("deduct")}
                            >
                                خصم
                            </button>
                        </div>
                    ) : null}

                    <div className="row row-cols-1 row-cols-md-2 g-2">
                        <div className="col">
                            <Form.Label>المبلغ (₪)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                            />
                        </div>
                        <div className="col">
                            <Form.Label>{isDeduction ? "سبب الخصم (مطلوب)" : "ملاحظة"}</Form.Label>
                            <Form.Control
                                type="text"
                                value={note}
                                required={isDeduction}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder={isDeduction ? "مثال: غرامة تأخير" : "مثال: دفعة نقدية"}
                            />
                        </div>
                    </div>

                    {after !== null ? (
                        <p className="wallet-panel__preview">
                            الرصيد بعد العملية:{" "}
                            <span
                                className={
                                    after < 0
                                        ? "wallet-panel__debit"
                                        : after === 0
                                          ? "wallet-panel__zero"
                                          : "wallet-panel__number"
                                }
                            >
                                {formatMoney(after)}
                            </span>
                        </p>
                    ) : null}

                    {/* The consequence, BEFORE the press: a deduction to ₪0 or
                        below stops this account taking new work. */}
                    {isDeduction && after !== null && after <= 0 && zeroWarning ? (
                        <div
                            className="wallet-panel__notice wallet-panel__notice--warning"
                            role="alert"
                        >
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            <div>
                                <p>{zeroWarning}</p>
                            </div>
                        </div>
                    ) : null}

                    <div className="buttons">
                        {/* Disabled while pending: a second press cannot send a
                            second movement while the first is still in flight. */}
                        <button className="button" type="submit" disabled={topUpPending}>
                            {submitLabel}
                        </button>
                    </div>
                </Form>
            ) : null}

            <h6 className="wallet-panel__history-title">سجل الحركات</h6>

            {!transactions || transactions.length === 0 ? (
                <p className="wallet-panel__empty">لا توجد حركات بعد</p>
            ) : (
                <div className="table-container wallet-panel__table">
                    <table>
                        <thead>
                            <tr>
                                <th>النوع</th>
                                <th>المبلغ</th>
                                <th>الرصيد بعدها</th>
                                <th>التفاصيل</th>
                                <th>بواسطة</th>
                                <th>التاريخ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((row) => {
                                const amountValue = Number(row.amount);
                                const actor = row.actor || row.admin || null;
                                const actorId = actor?.id ?? row.actorUserId ?? row.adminId ?? null;
                                const rate =
                                    row.orderId && row.rate !== null && row.rate !== undefined
                                        ? ` (${Number(row.rate)}%)`
                                        : "";
                                // An adjustment is ₪0 when a switch changed, and
                                // negative when an admin deducted money.
                                const typeLabel =
                                    row.type === "adjustment" && amountValue < 0
                                        ? "خصم"
                                        : typeLabels[row.type] || row.type;

                                return (
                                    <tr className="item" key={row.id}>
                                        <td>{typeLabel}</td>
                                        <td
                                            className={
                                                amountValue > 0
                                                    ? "wallet-panel__credit"
                                                    : amountValue < 0
                                                      ? "wallet-panel__debit"
                                                      : "wallet-panel__number"
                                            }
                                        >
                                            {/* The sign only where there is one:
                                                a zero note is not a credit. */}
                                            {amountValue > 0 ? "+" : ""}
                                            {formatMoney(amountValue)}
                                        </td>
                                        <td className="wallet-panel__number">
                                            {formatMoney(row.balance_after)}
                                        </td>
                                        <td className="wallet-panel__details">
                                            {row.orderId ? (
                                                linkOrders ? (
                                                    <NavLink to={`/orders/${row.orderId}`}>
                                                        طلب #{row.orderId}
                                                    </NavLink>
                                                ) : (
                                                    `طلب #${row.orderId}`
                                                )
                                            ) : null}
                                            {rate}
                                            {row.note ? ` ${row.note}` : null}
                                            {!row.orderId && !row.note ? "—" : null}
                                        </td>
                                        <td>
                                            {actor?.name || (actorId ? `#${actorId}` : "النظام")}
                                        </td>
                                        <td className="wallet-panel__date">
                                            <bdi dir="ltr">{formatDateTime(row.createdAt)}</bdi>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {pages > 1 ? (
                <div className="wallet-panel__pager">
                    <button
                        type="button"
                        className="cancel"
                        disabled={page <= 1}
                        onClick={() => onPageChange(page - 1)}
                    >
                        السابق
                    </button>
                    <span>
                        {page} / {pages}
                    </span>
                    <button
                        type="button"
                        className="cancel"
                        disabled={page >= pages}
                        onClick={() => onPageChange(page + 1)}
                    >
                        التالي
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export default WalletPanel;
